import { fetchOdooForm, submitOdooForm } from '../../actions/odooFormActions';
import { getQueryParams } from '../../utils/queryUtils';
import { scrollToFirstFormError } from '../../utils/scrollUtils';
import { getTileCMSData } from '../../utils/tileUtils';
import arrayMutators from 'final-form-arrays';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { Form } from 'react-final-form';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router';

function getFormtypeFromFormID(node, id) {
  for (const tile of node) {
    if (!tile) return null;
    if (tile.subTiles) {
      const type = getFormtypeFromFormID(tile.subTiles, id);
      if (type) return type;
    } else if (tile.formTypes) {
      for (const form of tile.formTypes) {
        if (form.id === id) return tile.type;
      }
    }
  }
  return null;
}

function applyEditedFields(base, editedFields) {
  let next = base;
  for (const { fieldName, hasManyGroup, rowIndex, fieldDetails, isDeleted } of Object.values(editedFields)) {
    if (isDeleted) {
      if (next[hasManyGroup]?.[rowIndex]) {
        const rows = [...next[hasManyGroup]];
        rows[rowIndex] = { isDeleted: true };
        next = { ...next, [hasManyGroup]: rows };
      }
    } else if (hasManyGroup && rowIndex != null) {
      const rows = [...(next[hasManyGroup] || [])];
      while (rows.length <= rowIndex) {
        rows.push({});
      }
      rows[rowIndex] = { ...rows[rowIndex], [fieldName]: fieldDetails };
      next = { ...next, [hasManyGroup]: rows };
    } else {
      next = { ...next, [fieldName]: fieldDetails };
    }
  }
  return next;
}

function OdooFormFields({
  handleSubmit,
  values,
  submitting,
  submitFailed,
  errors,
  submitErrors,
  odooForm,
  FormComponent,
  initialValues,
  pageClickHandler,
  lastPage,
  paymentPage,
  buttonLabel,
  buttonSubmittingLabel,
  subtitle,
  history,
}) {
  const hasAutoSubmittedRef = useRef(false);
  const errorsToUse = submitErrors || errors;

  useEffect(() => {
    if (submitFailed && errorsToUse) {
      scrollToFirstFormError(errorsToUse);
    }
  }, [submitFailed, errorsToUse]);

  useEffect(() => {
    const hasValues = values && Object.keys(values).length > 0;

    if (!paymentPage) {
      hasAutoSubmittedRef.current = false;
    }

    if (paymentPage && hasValues && !odooForm.isFetching && !odooForm.isSaving && !hasAutoSubmittedRef.current) {
      hasAutoSubmittedRef.current = true;
      handleSubmit();
    }
  }, [paymentPage, odooForm.isFetching, odooForm.isSaving, values, handleSubmit]);

  return (
    <FormComponent
      formData={odooForm.form}
      formValues={values}
      initialValues={initialValues}
      onSubmit={handleSubmit}
      submitting={submitting}
      pageClickHandler={pageClickHandler}
      lastPage={lastPage}
      paymentPage={paymentPage}
      buttonLabel={buttonLabel}
      buttonSubmittingLabel={buttonSubmittingLabel}
      subtitle={subtitle}
      history={history}
    />
  );
}

const OdooForm = ({ FormComponent, LoadingComponent, NotFoundComponent, subtitle: propSubtitle }) => {
  const dispatch = useDispatch();
  const odooForm = useSelector((state) => state.odooForm);
  const user = useSelector((state) => state.user);
  const editedFields = useSelector((state) => state.odooField.editedFields);
  const dashboardTiles = useSelector((state) => state.dashboard.tiles);
  const cmsTiles = useSelector((state) => state.config.content.tiles);

  const { formType, formId, formPage } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const history = useMemo(() => ({ push: navigate }), [navigate]);

  const initialValues = useMemo(() => {
    let result;
    if (!odooForm.form) {
      result = {};
    } else {
      result = {
        sessionId: user.sessionId,
        formType: odooForm.formType,
        formId: odooForm.formId,
        formPage: odooForm.formPage,
      };

      if (odooForm.form.page?.sections) {
        for (const section of odooForm.form.page.sections) {
          for (const fieldGroup of section.fieldGroups) {
            for (const field of fieldGroup.fields) {
              if (typeof field.value === 'undefined' || field.value === null || field.renderType === 'html_display') {
                continue;
              }
              if (fieldGroup.hasMany) {
                const numRows = Math.max(...fieldGroup.fields.map((f) => (Array.isArray(f.value) ? f.value.length : 0)));
                result[fieldGroup.name] = [];
                for (let n = 0; n < numRows; n++) {
                  const row = {};
                  for (const f of fieldGroup.fields) {
                    if (Array.isArray(f.value) && typeof f.value[n] !== 'undefined') {
                      row[f.fieldName] = f.value[n];
                    }
                  }
                  result[fieldGroup.name].push(row);
                }
              } else {
                result[field.fieldName] = field.value;
              }
            }
          }
        }
      }
    }

    for (const [key, value] of Object.entries(getQueryParams(location.search))) {
      result = { ...result, [key]: value.replace(/%20/g, ' ') };
    }

    return applyEditedFields(result, editedFields);
  }, [user.sessionId, odooForm.form, odooForm.formType, odooForm.formId, odooForm.formPage, location.search, editedFields]);

  const subtitle = useMemo(() => {
    let s = propSubtitle;
    const currentFormID = odooForm.formType || '';
    const formTypeCMS = getFormtypeFromFormID(dashboardTiles, currentFormID);
    if (formTypeCMS && !s) {
      const data = getTileCMSData(cmsTiles, formTypeCMS);
      if (data?.subtitle) {
        s = data.subtitle;
      }
    }
    return s;
  }, [propSubtitle, odooForm.formType, dashboardTiles, cmsTiles]);

  useEffect(() => {
    dispatch(fetchOdooForm(user.sessionId, formType, formId, formPage));
  }, [dispatch, user.sessionId, formType, formId, formPage]);

  const pageClickHandler = useCallback(
    (page, disabled) => {
      if (disabled) return;
      const formData = odooForm.form.form;
      for (const pageCode of formData.pageCodes) {
        if (pageCode.code === page.code) {
          navigate(`/form/${formData.code}/${formData.formToken}/${page.code}`);
        }
      }
    },
    [navigate, odooForm],
  );

  const submitHandler = useCallback(
    async (data) => {
      let transformedData = JSON.parse(JSON.stringify(data));

      for (const section of odooForm.form.page.sections) {
        for (const fieldGroup of section.fieldGroups) {
          if (!fieldGroup.hasMany || typeof transformedData[fieldGroup.name] === 'undefined') {
            continue;
          }

          for (let k = 0; k < data[fieldGroup.name].length; k++) {
            const row = data[fieldGroup.name][k];

            // If row is deleted, send the isDeleted flag to the server
            if (row && row.isDeleted) {
              const fieldName = `${fieldGroup.groupId}[${k}].isDeleted`;
              transformedData[fieldName] = true;
              continue;
            }

            for (const field of fieldGroup.fields) {
              if (field.renderType === 'html_display') {
                continue;
              }
              const fieldName = `${fieldGroup.groupId}[${k}].${field.fieldName}`;
              transformedData[fieldName] = row[field.fieldName];
            }
          }

          delete transformedData[fieldGroup.name];
        }
      }

      for (const key in transformedData) {
        if (transformedData[key] === false) {
          transformedData[key] = '';
        }
      }

      // Odoo does not care about it
      // But we want it in Api.php to intercept and generate a PDF for this form and save it to odoo.
      transformedData.formProcessPage = !!odooForm.form.page.formProcessPage;

      const result = await dispatch(submitOdooForm(transformedData, history));
      if (result && result._error) {
        return result;
      }
      return undefined;
    },
    [dispatch, history, odooForm],
  );

  if (odooForm.isFetching) {
    return <LoadingComponent />;
  }

  if (!odooForm.form) {
    return <NotFoundComponent />;
  }

  const lastPage = !!odooForm.form.page.formEndPage;
  const submitPage = !!odooForm.form.page.formProcessPage;
  const invoicePage = !!odooForm.form.page.formInvoicePage;
  const paymentPage = !!odooForm.form.page.payment;

  let buttonLabel = odooForm.form.page.buttonLabel || 'Save';
  let buttonSubmittingLabel = odooForm.form.page.buttonSubmittingLabel || 'Saving...';
  if (submitPage) {
    buttonLabel = 'Submit';
    buttonSubmittingLabel = 'Submitting...';
  }
  if (invoicePage) {
    buttonLabel = 'Pay now';
    buttonSubmittingLabel = 'Redirecting...';
  }

  return (
    <Form
      onSubmit={submitHandler}
      initialValues={initialValues}
      keepDirtyOnReinitialize
      mutators={{ ...arrayMutators }}
      render={({ handleSubmit, values, submitting, submitFailed, errors, submitErrors }) => (
        <OdooFormFields
          handleSubmit={handleSubmit}
          values={values}
          submitting={submitting}
          submitFailed={submitFailed}
          errors={errors}
          submitErrors={submitErrors}
          odooForm={odooForm}
          FormComponent={FormComponent}
          initialValues={initialValues}
          pageClickHandler={pageClickHandler}
          lastPage={lastPage}
          paymentPage={paymentPage}
          buttonLabel={buttonLabel}
          buttonSubmittingLabel={buttonSubmittingLabel}
          subtitle={subtitle}
          history={history}
        />
      )}
    />
  );
};

export default OdooForm;
